import { useDispatch, useSelector } from 'react-redux'
import { Button } from '../../../Components/Button/Button'
import { useLocalization } from '../../../Hooks/useLocalization'
import './CompanyList.scss'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { TextInput } from '../../../Components/TextInput/TextInput'
import { type RootState } from '../../../Store/Store'
import { useEffect, useMemo, useState } from 'react'
import {
  type CertificateCalculatedStatus,
  certificateStatuses,
  certificateStatusesExpired,
  certificateStatusesNotValidYet,
  setIsCertificatesRequested,
  setIsCompaniesRequested,
} from '../../../Store/DataSlice'
import { CompaniesTable } from '../CompaniesTable'
import { sortResultString } from '../../../Utils/Arrays'
import { SingleSelect } from '../../../Components/SingleSelect/SingleSelect'
import { isBelgianVat } from '../../../Utils/Validations'

const localizationSection = 'company'
const kboUpdateStatuses = [
  'nvt',
  'success',
  'failed-since',
  'failed-always',
] as const
type KboUpdateStatus = (typeof kboUpdateStatuses)[number]

export function CompanyList() {
  const dispatch = useDispatch()
  const localization = useLocalization(localizationSection)
  const certificateStatusesLocalization = useLocalization(
    'certificate-statuses'
  )
  const user = useSelector((state: RootState) => state.authentication.user)
  const isLoggedIn = !!user
  const isAdmin = user?.isAdmin ?? false

  // companies
  const isCompaniesRequested = useSelector(
    (state: RootState) => state.data.isCompaniesRequested
  )
  useEffect(() => {
    if (!isCompaniesRequested) dispatch(setIsCompaniesRequested(true))
  }, [isCompaniesRequested, dispatch])
  const allCompanies = useSelector((state: RootState) => state.data.companies)

  // certificates
  const isCertificatesRequested = useSelector(
    (state: RootState) => state.data.isCertificatesRequested
  )
  useEffect(() => {
    if (!isCertificatesRequested) dispatch(setIsCertificatesRequested(true))
  }, [isCertificatesRequested, dispatch])
  const allCertificates = useSelector(
    (state: RootState) => state.data.enrichedCertificates
  )

  // certificateCount per company vat
  const certificateDataPerCompanyVat = useMemo(() => {
    const result: Record<
      string,
      { count: number; calculatedStatuses: CertificateCalculatedStatus[] }
    > = {}
    for (const certificate of allCertificates) {
      for (const companyBranch of certificate.companyBranches) {
        const vatNumber = companyBranch.vatNumber
        if (result[vatNumber] == null)
          result[vatNumber] = { count: 0, calculatedStatuses: [] }
        result[vatNumber].count += 1
        if (
          !result[vatNumber].calculatedStatuses.includes(
            certificate.calculatedStatus
          )
        )
          result[vatNumber].calculatedStatuses.push(
            certificate.calculatedStatus
          )
      }
    }
    return result
  }, [allCertificates])

  const [filterVatNumber, setFilterVatNumber] = useState('')
  const [filterName, setFilterName] = useState('')
  const [filterCertificateStatus, setFilterCertificateStatus] =
    useState<CertificateCalculatedStatus>('')
  const [kboUpdateStatus, setKboUpdateStatus] = useState<KboUpdateStatus | ''>(
    ''
  )

  const filteredCompanies = useMemo(() => {
    return allCompanies
      .filter((c) => {
        if (
          filterVatNumber &&
          !c.vatNumber.toLowerCase().includes(filterVatNumber.toLowerCase())
        )
          return false
        if (
          filterName &&
          !c.name.toLowerCase().includes(filterName.toLowerCase())
        )
          return false
        if (!isLoggedIn && certificateDataPerCompanyVat[c.vatNumber] == null)
          return false

        if (
          filterCertificateStatus &&
          (certificateDataPerCompanyVat[c.vatNumber] == null ||
            !certificateDataPerCompanyVat[
              c.vatNumber
            ].calculatedStatuses.includes(filterCertificateStatus))
        )
          return false

        if (kboUpdateStatus !== '') {
          switch (kboUpdateStatus) {
            case 'nvt': {
              if (isBelgianVat(c.vatNumber)) return false
              break
            }
            case 'success': {
              if (!isBelgianVat(c.vatNumber) || c.lastKpbCheckSuccess !== true)
                return false
              break
            }
            case 'failed-since': {
              if (
                !isBelgianVat(c.vatNumber) ||
                c.lastKpbCheckSuccess === true ||
                c.lastKpbUpdate == null
              )
                return false
              break
            }
            case 'failed-always': {
              if (
                !isBelgianVat(c.vatNumber) ||
                c.lastKpbCheckSuccess === true ||
                c.lastKpbUpdate != null
              )
                return false
              break
            }
          }
        }
        return true
      })
      .sort((a, b) => sortResultString(a.vatNumber, b.vatNumber))
  }, [
    allCompanies,
    filterVatNumber,
    filterName,
    isLoggedIn,
    certificateDataPerCompanyVat,
    filterCertificateStatus,
    kboUpdateStatus,
  ])

  return (
    <div className="company-list">
      {isLoggedIn ? (
        <div className="button-row">
          <Button
            importance={'primary'}
            text={localization.text('add-company', 'Voeg onderneming toe')}
            onClick={() =>
              dispatch(
                setDestinationView({
                  destinationView: 'company-add',
                })
              )
            }
          />
        </div>
      ) : null}
      <div className="filters">
        <div className="filter-field">
          <TextInput
            label={localization.text(
              'filter-vat-number-input-label',
              'Ondernemingsnummer'
            )}
            helperText={localization.text(
              'filter-vat-number-input-helper',
              'Geef (een deel van) de het ondernemingsnummer in'
            )}
            value={filterVatNumber}
            onChange={setFilterVatNumber}
          />
        </div>
        <div className="filter-field">
          <TextInput
            label={localization.text(
              'filter-company-input-label',
              'Onderneming'
            )}
            helperText={localization.text(
              'filter-company-input-helper',
              'Geef (een deel van) de naam van de onderneming in'
            )}
            value={filterName}
            onChange={setFilterName}
          />
        </div>
        {isAdmin && (
          <div className="filter-field">
            <SingleSelect
              label={localization.text(
                'filter-certificate-status-input-label',
                'Certificaat status'
              )}
              helperText={localization.text(
                'filter-certificate-status-input-helper',
                'Moet minstens één certificaat met deze status hebben'
              )}
              items={[
                '',
                certificateStatusesExpired,
                certificateStatusesNotValidYet,
                ...certificateStatuses,
              ].map((status) => ({
                label: certificateStatusesLocalization.text(status, status),
                value: status,
              }))}
              value={filterCertificateStatus}
              onChange={(newValue) => {
                setFilterCertificateStatus(
                  newValue as CertificateCalculatedStatus
                )
              }}
              isRequired={true}
            />
          </div>
        )}
        {isAdmin && (
          <div className="filter-field">
            <SingleSelect
              label={localization.text(
                'filter-kbo-update-status-input-label',
                'KBO update status'
              )}
              helperText={localization.text(
                'filter-kbo-update-status-input-helper',
                'De Kbo update status'
              )}
              items={['', ...kboUpdateStatuses].map((status) => ({
                label:
                  status === ''
                    ? ''
                    : localization.text(`kbo-update-status-${status}`, status),
                value: status,
              }))}
              value={kboUpdateStatus}
              onChange={(newValue) => {
                setKboUpdateStatus(newValue as KboUpdateStatus)
              }}
              isRequired={true}
            />
          </div>
        )}
      </div>
      <CompaniesTable
        companies={filteredCompanies}
        isLoading={allCompanies.length === 0}
        showAdminColumns={isAdmin}
      />
    </div>
  )
}
